import React, { useState, useRef } from 'react';
import { useToast } from '../hooks/useToast';
import db from '../lib/database';
import {
    processImageForUpload,
    uploadInPool,
    UPLOAD_CONCURRENCY,
} from '../lib/imageUtils';
import { ChoiceKeys, Combobox } from './ui';

// Office-side photo uploader. Used by:
//   - ClientIntakeModal "Photo Inventory" section
//   - ClientsPage (after the duplicate local copy was removed)
//
// History: this component was extracted from index.html into its own file in
// commit c2b9c65 (April 7, 2026) during the SPA refactor, but the extraction
// shipped a stub — the per-file loop only logged to console and bumped a
// counter, never calling db.uploadPhoto. Five weeks of silent data loss for
// any office staff dropping photos onto this surface. (Nobody noticed
// because estimators were uploading via the walkthrough portal, which DID
// work, and those photos showed up in the client file via the shared
// client_id query on mount.)
//
// This version uses the same pipeline as the walkthrough portal:
// HEIC → JPEG → 2400px JPEG q=0.85 → Supabase, 3 concurrent. Per-batch
// progress + aggregated final toast.

const ROOM_TAGS = ['Living Room', 'Bedroom', 'Kitchen', 'Bathroom', 'Garage', 'Office', 'Dining Room', 'Basement', 'Attic', 'Outdoor', 'Storage', 'Other'];
const PHOTO_SOURCES = [
    { value: 'office', label: 'Office Upload' },
    { value: 'client', label: 'Client Submitted' },
    { value: 'estimator', label: 'Estimator Walkthrough' },
    { value: 'crew', label: 'Crew / Move Day' },
];

export default function PhotoUpload({ clientId, onUploaded }) {
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [source, setSource] = useState('office');
    const [roomTag, setRoomTag] = useState('');
    const [batchLabel, setBatchLabel] = useState('');
    // Per-batch progress: { done, total, failed } — done is succeeded+failed
    // since both contribute to the progress bar. We display "Uploading X of
    // Y... (N failed)" in the label.
    const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0 });
    const fileInputRef = useRef(null);
    const { show } = useToast();
    const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

    const handleFiles = async (files) => {
        if (!files?.length) return;
        const allFiles = Array.from(files);
        // ProRAW .dng files can't be decoded in the browser — flag explicitly
        // rather than silently dropping. Common confusion when an iPhone user
        // has ProRAW on by default.
        const dngFiles = allFiles.filter(f => /\.dng$/i.test(f.name));
        if (dngFiles.length > 0) {
            show(`${dngFiles.length} DNG (ProRAW) file(s) skipped — DNG can't be processed in the browser. Turn off ProRAW in iPhone Settings → Camera → Formats, or use JPG/HEIC versions.`, 'warning');
        }
        const supportedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
        const supportedExts = /\.(jpg|jpeg|png|webp|heic|heif)$/i;
        const imageFiles = allFiles.filter(f =>
            (supportedTypes.includes(f.type) || supportedExts.test(f.name)) &&
            !/\.dng$/i.test(f.name)
        );
        if (!imageFiles.length) {
            show('No supported images. Use JPG, PNG, HEIC, or WEBP.', 'error');
            return;
        }

        setUploading(true);
        const total = imageFiles.length;
        let succeeded = 0;
        let failed = 0;
        setProgress({ done: 0, total, failed: 0 });

        const batchId = `office_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const batchLabelToUse = batchLabel || `${PHOTO_SOURCES.find(s => s.value === source)?.label || 'Office'} ${new Date().toLocaleDateString()}`;

        // Picker order survives parallel uploads (sql/089, 2026-09-11):
        // each photo is stamped batchStart + its index in the selection,
        // so galleries sort the way the files were picked, not the order
        // the uploads happened to finish.
        const batchStart = Date.now();
        const worker = async (file, index) => {
            let ok = false;
            try {
                const processed = await processImageForUpload(file);
                const photo = await db.uploadPhoto(processed, clientId, {
                    room_tags: roomTag ? [roomTag] : [],
                    source,
                    upload_batch_id: batchId,
                    batch_label: batchLabelToUse,
                    sort_order: batchStart + index,
                });
                // Parent's handler appends/prepends to its own photos state.
                // We don't manage gallery state here — the parent owns it.
                if (typeof onUploaded === 'function') onUploaded(photo);
                ok = true;
            } catch (e) {
                console.error('Upload failed for', file.name, e);
            } finally {
                if (ok) succeeded++; else failed++;
                setProgress({ done: succeeded + failed, total, failed });
            }
        };

        await uploadInPool(imageFiles, worker, UPLOAD_CONCURRENCY);

        setUploading(false);
        setProgress({ done: 0, total: 0, failed: 0 });
        setBatchLabel('');
        if (fileInputRef.current) fileInputRef.current.value = '';

        if (failed === 0) {
            show(`${succeeded} photo${succeeded !== 1 ? 's' : ''} uploaded`);
        } else if (succeeded === 0) {
            show(`All ${total} uploads failed`, 'error');
        } else {
            show(`${succeeded} of ${total} uploaded — ${failed} failed`, 'warning');
        }
    };

    // Width transition uses --ease-out so the bar feels responsive as it
    // grows; 200ms keeps it snappy without looking nervous.
    const progressPct = progress.total > 0
        ? Math.round((progress.done / progress.total) * 100)
        : 0;
    const progressLabel = progress.done >= progress.total
        ? 'Finishing up…'
        : `Uploading ${progress.done + 1} of ${progress.total}…${progress.failed ? ` (${progress.failed} failed)` : ''}`;

    return (
        <div className="space-y-3">
            <div className="flex gap-3 flex-wrap">
                <ChoiceKeys items={PHOTO_SOURCES} value={source} onChange={v => setSource(v)} size="sm" ariaLabel="Photo source" />
                <Combobox
                    items={ROOM_TAGS.map(r => ({ value: r, label: r }))}
                    value={roomTag}
                    onChange={v => setRoomTag(v)}
                    placeholder="No room tag"
                    ariaLabel="Room tag"
                    size="sm"
                    className="w-[160px]"
                />
                <input className={inputCls} value={batchLabel} onChange={e => setBatchLabel(e.target.value)} placeholder="Batch label (optional)" style={{maxWidth: 220}} />
            </div>
            <div
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${dragOver ? 'border-red-400 bg-red-50' : 'border-slate-300 hover:border-red-300 hover:bg-slate-50'}`}
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
            >
                {uploading ? (
                    <div className="space-y-3">
                        <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                            <span className="font-medium">{progressLabel}</span>
                            <span className="text-slate-400 text-xs">({progressPct}%)</span>
                        </div>
                        {/* Determinate progress bar. Width is the only animated
                            property — runs on the GPU. */}
                        <div className="relative h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-red-600"
                                style={{
                                    width: `${progressPct}%`,
                                    transition: 'width 200ms var(--ease-out)',
                                }}
                                role="progressbar"
                                aria-valuenow={progressPct}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            />
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="text-3xl mb-2">📷</div>
                        <p className="text-sm font-medium text-slate-700">Drop photos here or click to browse</p>
                        <p className="text-xs text-slate-400 mt-1">All iPhone formats supported — auto-resized to 2400px JPEG for fast upload</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">Having trouble uploading? Try splitting into smaller batches.</p>
                    </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif" multiple className="hidden" onChange={e => handleFiles(e.target.files)} />
            </div>
        </div>
    );
}
